import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useState } from "react";
import { CustomEvent } from "schemas/functions";
import theme from "styles/theme";

import FileUpload from "components/Attachments/FileUpload";
import { fetcherAuth } from "utils/api";

interface BugReportDialogProps {
  open: boolean;
  handleClose: () => void;
}

export default function BugReportDialog({
  open,
  handleClose,
}: BugReportDialogProps) {
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setAlert, setErrorAlert } = useContext(AlertContext);

  const [saveLoading, setSaveLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState("");
  const [validation, setValidation] = useState("");

  const handleChange = (e: CustomEvent) => {
    setDescription(e.target.value);
    setValidation("");
  };

  const handleSubmit = async () => {
    if (!currentOrg?.id) {
      return;
    }

    if (!(description?.trim().length > 0)) {
      setValidation("Please fill out this field.");
      return;
    }

    const data = new FormData();
    if (file) {
      data.append("file", file);
    }
    data.append("description", JSON.stringify(description));

    setSaveLoading(true);

    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/users/report-bug`,
        "POST",
        {},
        data,
        false,
        true,
      );
      setAlert("Successfully reported bug", "success");
      close();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSaveLoading(false);
    }
  };

  const close = () => {
    setFile(null);
    setDescription("");
    handleClose();
  };

  return (
    <Dialog open={open} onClose={close} fullScreen={isMobileScreen}>
      <DialogTitle>
        Ask a Question{" "}
        <Box component="i" className="fa-solid fa-circle-exclamation" />
      </DialogTitle>
      <DialogContent>
        Please share your question or describe the issue you are enountering: *
        <TextField
          placeholder="Ask your question here..."
          fullWidth
          multiline
          rows={10}
          sx={{ mt: 1, mb: 2 }}
          required
          onChange={handleChange}
          error={!!validation}
        />
        {validation && (
          <FormHelperText sx={{ color: "error.main" }}>
            {validation}
          </FormHelperText>
        )}
        Attach image (optional)
        <Grid container alignItems="center" sx={{ mt: 2 }}>
          {file && (
            <Grid>
              <Box
                component="img"
                alt="img preview"
                src={file ? URL.createObjectURL(file) : ""}
                sx={{ maxWidth: { xs: 300, md: 400 }, mb: 2 }}
              />
              <Grid item xs={12}>
                <Button
                  sx={{
                    fontSize: 12,
                    textTransform: "none",
                    color: "error.main",
                  }}
                  onClick={() => setFile(null)}
                >
                  Remove Image{" "}
                  <Box
                    component="i"
                    className="fa-regular fa-trash"
                    sx={{ ml: 0.5 }}
                  />
                </Button>{" "}
              </Grid>
            </Grid>
          )}

          {!file && (
            <FileUpload
              id={`bug-report`}
              name="bugreport"
              setFile={setFile}
              setAlert={setAlert}
              limit={25000000} // 25 MB
              buttonText={
                <>
                  Upload Image{" "}
                  <Box
                    sx={{ ml: 0.5 }}
                    component="i"
                    className="fa-regular fa-upload"
                  />
                </>
              }
              acceptType={"image/*"}
              allowPaste
            />
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} color="secondary">
          Close
        </Button>
        <LoadingButton onClick={handleSubmit} loading={saveLoading}>
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
